/**
 * Top-level cross-organization calendar view.
 *
 * Read-only filterable feed of calendar days from every district that has
 * published a calendar. Filters: districts (multi-select), special day
 * types (multi-select), date range. Gated by the calendar.pageEnabled
 * feature flag — when off, the route is hidden in nav and this page just
 * shows a not-enabled message.
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'flowbite-react';
import {
  HiCalendar,
  HiColorSwatch,
  HiDownload,
  HiUserGroup,
  HiViewBoards,
  HiViewList,
} from 'react-icons/hi';

import {
  calendarApi,
  leasApi,
  getUserErrorMessage,
  type CalendarFeedItem,
} from '../api';
import { descriptorsApi, type Descriptor } from '../api/descriptors';
import { dateInputBounds } from '../utils/dateValidation';
import type { LEAListItem } from '../types/api';
import {
  SearchableMultiSelect,
  type MultiSelectOption,
} from '../components/common/SearchableMultiSelect';
import { Loading } from '../components/common/Loading';
import { PageHeader } from '../components/common/PageHeader';
import { useAppSettings, APP_SETTING_KEYS } from '../contexts/AppSettingsContext';
import {
  BOUND_DAY_TYPE_OPTIONS,
  CalendarMonthGrid,
  TYPE_BADGE_CLASSNAMES,
  TYPE_LABELS,
} from '../components/calendar/CalendarMonthGrid';
import { CalendarMatrixView } from '../components/calendar/CalendarMatrixView';

const VIEW_STORAGE_KEY = 'aiu.calendarView';
type CalendarViewMode = 'calendar' | 'list' | 'matrix';

function loadStoredView(): CalendarViewMode {
  if (typeof window === 'undefined') return 'calendar';
  const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return v === 'list' || v === 'matrix' || v === 'calendar' ? v : 'calendar';
}

const EXPAND_STORAGE_KEY = 'aiu.calendarExpandDistricts';

function loadStoredExpand(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(EXPAND_STORAGE_KEY) === 'true';
}

function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function monthKey(iso: string): string {
  // YYYY-MM
  return iso.slice(0, 7);
}

function formatMonthLabel(monthIso: string): string {
  const [y, m] = monthIso.split('-').map(Number);
  if (!y || !m) return monthIso;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

export const CalendarPage = () => {
  const { getBool } = useAppSettings();
  const pageEnabled = getBool(APP_SETTING_KEYS.calendarPageEnabled, false);

  const [districts, setDistricts] = useState<LEAListItem[]>([]);
  const [specialDayTypes, setSpecialDayTypes] = useState<Descriptor[]>([]);
  const [feed, setFeed] = useState<CalendarFeedItem[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [selectedDistrictIds, setSelectedDistrictIds] = useState<number[]>([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState<CalendarViewMode>(loadStoredView);
  const [expandByOrg, setExpandByOrg] = useState<boolean>(loadStoredExpand);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    }
  }, [view]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(EXPAND_STORAGE_KEY, String(expandByOrg));
    }
  }, [expandByOrg]);

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const blob = await calendarApi.exportXlsx({
        districts: selectedDistrictIds.length ? selectedDistrictIds : undefined,
        types: selectedTypeIds.length ? selectedTypeIds : undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aiu-directory-calendar-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  // Load filter source data
  useEffect(() => {
    if (!pageEnabled) {
      setLoadingMeta(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [leas, types] = await Promise.all([
          leasApi.list({ limit: 500 }),
          descriptorsApi.getByTypeName('Special Day Type'),
        ]);
        if (cancelled) return;
        setDistricts(leas);
        setSpecialDayTypes(
          [...types]
            .filter((t) => t.active)
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        );
      } catch (err) {
        if (!cancelled) setError(getUserErrorMessage(err));
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageEnabled]);

  // Run query whenever filters change
  useEffect(() => {
    if (!pageEnabled) return;
    let cancelled = false;
    setLoadingFeed(true);
    setError(null);
    calendarApi
      .list({
        districts: selectedDistrictIds.length ? selectedDistrictIds : undefined,
        types: selectedTypeIds.length ? selectedTypeIds : undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
      })
      .then((rows) => {
        if (!cancelled) setFeed(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(getUserErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingFeed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageEnabled, selectedDistrictIds, selectedTypeIds, fromDate, toDate]);

  const districtOptions: MultiSelectOption[] = useMemo(
    () =>
      districts.map((d) => ({
        id: d.educationOrganizationId,
        label: d.shortNameOfInstitution || d.nameOfInstitution,
      })),
    [districts]
  );

  const typeOptions: MultiSelectOption[] = useMemo(
    () => [
      ...specialDayTypes.map((t) => ({
        id: t.descriptorId,
        label: t.shortDescription,
      })),
      // Synthetic bound-day markers (negative sentinel IDs) are filterable too.
      ...BOUND_DAY_TYPE_OPTIONS.map((b) => ({ id: b.id, label: b.label })),
    ],
    [specialDayTypes]
  );

  const groupedByMonth = useMemo(() => {
    const groups = new Map<string, CalendarFeedItem[]>();
    for (const item of feed) {
      const key = monthKey(item.calendarDate);
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [feed]);

  if (!pageEnabled) {
    return (
      <div className="container mx-auto p-4">
        <PageHeader title="Calendar" />
        <Alert color="info">
          The cross-district calendar page is not yet enabled. A super admin
          can turn it on from the App Settings page (key
          <code className="mx-1">calendar.pageEnabled</code>).
        </Alert>
      </div>
    );
  }

  if (loadingMeta) return <Loading />;

  return (
    <div className="container mx-auto p-4 space-y-4">
      <PageHeader
        title="Calendar"
        subtitle="Special days across all districts. Use the filters to narrow by district, day type, or date range."
        actions={
          <Button
            color="light"
            onClick={handleExport}
            disabled={exporting || feed.length === 0}
            title={
              feed.length === 0
                ? 'No matching events to export'
                : 'Download a Power BI-style matrix (districts × dates) as XLSX'
            }
          >
            <HiDownload className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        }
      />

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Districts
            </label>
            <SearchableMultiSelect
              options={districtOptions}
              selectedIds={selectedDistrictIds}
              onChange={setSelectedDistrictIds}
              placeholder="All districts"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Day types
            </label>
            <SearchableMultiSelect
              options={typeOptions}
              selectedIds={selectedTypeIds}
              onChange={setSelectedTypeIds}
              placeholder="All types"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="filterFromDate"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                From
              </label>
              <input
                id="filterFromDate"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                {...dateInputBounds}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="filterToDate"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                To
              </label>
              <input
                id="filterToDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                {...dateInputBounds}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm"
              />
            </div>
          </div>
        </div>
        {(selectedDistrictIds.length ||
          selectedTypeIds.length ||
          fromDate ||
          toDate) && (
          <div className="mt-3 flex justify-end">
            <Button
              size="xs"
              color="light"
              onClick={() => {
                setSelectedDistrictIds([]);
                setSelectedTypeIds([]);
                setFromDate('');
                setToDate('');
              }}
            >
              Clear filters
            </Button>
          </div>
        )}
      </Card>

      {loadingFeed ? (
        <Loading />
      ) : feed.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <HiCalendar className="mx-auto h-10 w-10 text-gray-300 mb-2" />
            <p>No matching calendar events.</p>
            <p className="text-sm">Try widening your filters.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {feed.length} {feed.length === 1 ? 'event' : 'events'} across{' '}
              {new Set(feed.map((f) => f.educationOrganizationId)).size}{' '}
              {new Set(feed.map((f) => f.educationOrganizationId)).size === 1
                ? 'district'
                : 'districts'}
              .
            </p>

            {/* View toggle: Calendar / List / Matrix */}
            <div
              role="tablist"
              aria-label="Choose how to display the events"
              className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm"
            >
              {([
                { id: 'calendar', label: 'Calendar', icon: HiCalendar },
                { id: 'list', label: 'List', icon: HiViewList },
                { id: 'matrix', label: 'Matrix', icon: HiViewBoards },
              ] as const).map(({ id, label, icon: Icon }) => {
                const active = view === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setView(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset ${
                      active
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Calendar view */}
          {view === 'calendar' && (
          <Card>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Month view
              </h4>
              {/* Action button — label/icon describe what the next click does. */}
              <button
                type="button"
                onClick={() => setExpandByOrg((v) => !v)}
                title={
                  expandByOrg
                    ? 'Group multi-district days into one chip per day type'
                    : 'Show a separate chip for each district'
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {expandByOrg ? (
                  <>
                    <HiColorSwatch className="h-4 w-4" />
                    Group by type
                  </>
                ) : (
                  <>
                    <HiUserGroup className="h-4 w-4" />
                    Show each district
                  </>
                )}
              </button>
            </div>
            <CalendarMonthGrid
              events={feed.map((f) => ({
                date: f.calendarDate,
                typeCode: f.specialDayTypeCode ?? 'Unknown',
                orgLabel: f.organizationShortName ?? f.organizationName ?? undefined,
              }))}
              fromDate={
                fromDate
                  ? (() => {
                      const [y, m, d] = fromDate.split('-').map(Number);
                      return new Date(y, m - 1, d);
                    })()
                  : undefined
              }
              toDate={
                toDate
                  ? (() => {
                      const [y, m, d] = toDate.split('-').map(Number);
                      return new Date(y, m - 1, d);
                    })()
                  : undefined
              }
              expandByOrg={expandByOrg}
            />
          </Card>
          )}

          {/* Matrix view */}
          {view === 'matrix' && (
            <Card>
              <CalendarMatrixView
                events={feed}
                fromDate={
                  fromDate
                    ? (() => {
                        const [y, m, d] = fromDate.split('-').map(Number);
                        return new Date(y, m - 1, d);
                      })()
                    : undefined
                }
                toDate={
                  toDate
                    ? (() => {
                        const [y, m, d] = toDate.split('-').map(Number);
                        return new Date(y, m - 1, d);
                      })()
                    : undefined
                }
              />
            </Card>
          )}

          {/* List view */}
          {view === 'list' &&
          groupedByMonth.map(([month, items]) => {
            // Within this month, consolidate by date → by type → list of orgs.
            const byDate = new Map<
              string,
              Map<string, Array<{ orgName: string; orgId: number; rowId: number }>>
            >();
            for (const item of items) {
              const code = item.specialDayTypeCode ?? 'Unknown';
              if (!byDate.has(item.calendarDate)) {
                byDate.set(item.calendarDate, new Map());
              }
              const typeMap = byDate.get(item.calendarDate)!;
              const list = typeMap.get(code) ?? [];
              list.push({
                orgName:
                  item.organizationShortName ?? item.organizationName ?? '—',
                orgId: item.educationOrganizationId,
                rowId: item.educationOrganizationCalendarDayId,
              });
              typeMap.set(code, list);
            }
            const sortedDates = Array.from(byDate.entries()).sort(([a], [b]) =>
              a.localeCompare(b)
            );

            return (
              <Card key={month}>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {formatMonthLabel(month)}{' '}
                  <span className="font-normal text-gray-500 dark:text-gray-400 text-sm">
                    ({items.length})
                  </span>
                </h3>
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sortedDates.map(([date, typeMap]) => (
                    <li key={date} className="py-2">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="text-sm font-mono text-gray-500 dark:text-gray-400 min-w-[10rem] shrink-0">
                          {formatIsoDate(date)}
                        </span>
                        <div className="flex-1 flex flex-col gap-1">
                          {Array.from(typeMap.entries())
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([code, orgs]) => {
                              const badgeClass =
                                TYPE_BADGE_CLASSNAMES[code] ??
                                'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
                              const label = TYPE_LABELS[code] ?? code;
                              return (
                                <div
                                  key={`${date}-${code}`}
                                  className="flex flex-wrap items-baseline gap-2"
                                >
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded shrink-0 ${badgeClass}`}
                                  >
                                    {label}{' '}
                                    {orgs.length > 1 && (
                                      <span className="font-semibold">
                                        ({orgs.length})
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {orgs
                                      .map((o) => o.orgName)
                                      .sort((a, b) => a.localeCompare(b))
                                      .join(', ')}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
